import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AiModelsService } from './aiModelsApi';
import { IPaginationPayload } from '../../../../interfaces/shared/IPaginationPayload';
import { IAiModelsListState } from '../../../../interfaces/ai-models/IAiModelsList';
import { PaginatedAiModels } from '../../../../interfaces/ai-models/paginatedStore.type';
import { AxiosError } from 'axios';

const initialState: IAiModelsListState = {
    aiModelsAreLoading: false,
    aiModelIsLoading: false,
    allModelsAreLoading: false,
    requestStatus: 'default',
    message: '',
};

export const tryToFetchAllAiModels = createAsyncThunk<any, any>(
    'aiModels/tryToFetchAllAiModels',
    async (data: any) => {
        const result = await AiModelsService.tryToFetchAllAiModels(data);
        return result?.data;
    }
);

export const tryToFetchAiModels = createAsyncThunk<PaginatedAiModels, IPaginationPayload>(
    'aiModels/tryToFetchAiModels',
    async ({ pageNumber, pageSize, filters, data }) => {
        const result = await AiModelsService.tryToFetchAiModels(
            pageNumber,
            pageSize,
            data,
            filters,
        );
        return result?.data;
    },
);

export const tryToFetchSingleAiModel = createAsyncThunk<any, string>(
    'aiModels/tryToFetchSingleAiModel',
    async (id) => {
        const result = await AiModelsService.tryToFetchSingleAiModel(id);
        return result?.data?.data;
    }
);

export const tryToDeleteAiModel = createAsyncThunk<any, any>(
    'aiModels/tryToDeleteAiModel',
    async (data) => {
        const result = await AiModelsService.tryToDeleteAiModel(data);
        return result?.data;
    }
);

export const tryToEditAiModel = createAsyncThunk<any, any>(
    'aiModels/tryToEditAiModel',
    async (data, { rejectWithValue }) => {
        try {
            const result = await AiModelsService.tryToEditAiModel(data);
            return result?.data?.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToCreateAiModel = createAsyncThunk<any, any>(
    'aiModels/tryToCreateAiModel',
    async (data, { rejectWithValue }) => {
        try {
            const result = await AiModelsService.tryToCreateAiModel(data);
            return result?.data?.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const aiModelsSlice = createSlice({
    name: 'aiModels',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // tryToFetchAllAiModels
            .addCase(tryToFetchAllAiModels.pending, (state) => {
                state.allModelsAreLoading = true;
            })
            .addCase(tryToFetchAllAiModels.rejected, (state) => {
                state.allModelsAreLoading = false;
            })
            .addCase(tryToFetchAllAiModels.fulfilled, (state, action) => {
                state.allModelsAreLoading = false;
                state.allAiModels = action.payload;
            })

            // tryToFetchAiModels
            .addCase(tryToFetchAiModels.pending, (state) => {
                state.aiModelsAreLoading = true;
            })
            .addCase(tryToFetchAiModels.rejected, (state) => {
                state.aiModelsAreLoading = false;
            })
            .addCase(tryToFetchAiModels.fulfilled, (state, action) => {
                state.aiModelsAreLoading = false;
                state.aiModels = action.payload;
            })


            // tryToFetchSingleAiModel
            .addCase(tryToFetchSingleAiModel.pending, (state) => {
                state.aiModelIsLoading = true;
            })
            .addCase(tryToFetchSingleAiModel.rejected, (state) => {
                state.aiModelIsLoading = false;
            })
            .addCase(tryToFetchSingleAiModel.fulfilled, (state, action) => {
                state.aiModelIsLoading = false;
                state.aiModel = action.payload;
            })

            // tryToDeleteAiModel
            .addCase(tryToDeleteAiModel.pending, (state) => {
                state.aiModelIsLoading = true;
            })
            .addCase(tryToDeleteAiModel.rejected, (state) => {
                state.aiModelIsLoading = false;
            })
            .addCase(tryToDeleteAiModel.fulfilled, (state, action) => {
                state.aiModelIsLoading = false;
                if (state.aiModels?.data.elements)
                    state.aiModels.data.elements =
                        { ...state }.aiModels?.data.elements.filter((model) => model?._id !== action?.meta?.arg?.id) || [];
            })

            // tryToEditAiModel
            .addCase(tryToEditAiModel.pending, (state) => {
                state.aiModelIsLoading = true;
            })
            .addCase(tryToEditAiModel.rejected, (state) => {
                state.aiModelIsLoading = false;
            })
            .addCase(tryToEditAiModel.fulfilled, (state, action) => {
                state.aiModelIsLoading = false;
                state.requestStatus = 'success';
                if (state.aiModels?.data.elements)
                    state.aiModels.data.elements =
                        { ...state }.aiModels?.data.elements.map((model) => {
                            if (model?._id === action.meta?.arg?.id) {
                                return {
                                    ...action?.payload?.data || {},
                                };
                            } else {
                                return model;
                            }
                        }) || [];
            })


            // tryToCreateAiModel
            .addCase(tryToCreateAiModel.pending, (state) => {
                state.aiModelIsLoading = true;
            })
            .addCase(tryToCreateAiModel.rejected, (state) => {
                state.aiModelIsLoading = false;
            })
            .addCase(tryToCreateAiModel.fulfilled, (state, action) => {
                state.aiModelIsLoading = false;
                state.requestStatus = 'success';
                const data = action.payload.data;
                if (state.aiModels?.data)
                    state.aiModels.data.elements = [
                        ...state.aiModels?.data.elements,
                        data,
                    ];
            })

    },
});

export default aiModelsSlice.reducer;